import React, { useEffect, useState } from 'react';
import type { BillingClient } from '../../types';

interface BillingClientModalProps {
  client: BillingClient | null;
  onClose: () => void;
  onSave: (client: BillingClient) => void | Promise<void>;
}

const empty = {
  name: '',
  contactPerson: '',
  email: '',
  phone: '',
  address: '',
  gstNumber: '',
  paymentTerms: '',
  defaultBreakfastCoverPrice: '',
  notes: '',
};

const BillingClientModal: React.FC<BillingClientModalProps> = ({ client, onClose, onSave }) => {
  const [form, setForm] = useState(empty);
  const [id, setId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name,
        contactPerson: client.contactPerson ?? '',
        email: client.email ?? '',
        phone: client.phone ?? '',
        address: client.address ?? '',
        gstNumber: client.gstNumber ?? '',
        paymentTerms: client.paymentTerms ?? '',
        defaultBreakfastCoverPrice:
          client.defaultBreakfastCoverPrice != null
            ? String(client.defaultBreakfastCoverPrice)
            : '',
        notes: client.notes ?? '',
      });
      setId(client.id);
    } else {
      setForm(empty);
      setId(null);
    }
  }, [client]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (saving) return;
    if (!form.name.trim()) {
      alert('Client name is required.');
      return;
    }
    const payload: BillingClient = { id: id || 0, name: form.name.trim() };
    if (form.contactPerson.trim()) payload.contactPerson = form.contactPerson.trim();
    if (form.email.trim()) payload.email = form.email.trim();
    if (form.phone.trim()) payload.phone = form.phone.trim();
    if (form.address.trim()) payload.address = form.address.trim();
    if (form.gstNumber.trim()) payload.gstNumber = form.gstNumber.trim();
    if (form.paymentTerms.trim()) payload.paymentTerms = form.paymentTerms.trim();
    const cover = parseFloat(form.defaultBreakfastCoverPrice);
    if (Number.isFinite(cover) && cover > 0) payload.defaultBreakfastCoverPrice = cover;
    if (form.notes.trim()) payload.notes = form.notes.trim();
    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const blockEnterSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl" data-soft-keyboard-handled>
        <h2 className="text-xl font-bold text-gray-900">
          {client ? 'Edit billing client' : 'Add billing client'}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Clients you invoice for services such as breakfast covers.
        </p>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Client name *</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              onKeyDown={blockEnterSubmit}
              enterKeyHint="next"
              data-soft-keyboard-handled
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Contact person</label>
            <input
              name="contactPerson"
              value={form.contactPerson}
              onChange={handleChange}
              onKeyDown={blockEnterSubmit}
              enterKeyHint="next"
              data-soft-keyboard-handled
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                onKeyDown={blockEnterSubmit}
                enterKeyHint="next"
                data-soft-keyboard-handled
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                onKeyDown={blockEnterSubmit}
                enterKeyHint="next"
                data-soft-keyboard-handled
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Billing address</label>
            <textarea
              name="address"
              rows={2}
              value={form.address}
              onChange={handleChange}
              data-soft-keyboard-handled
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Client GST #</label>
              <input
                name="gstNumber"
                value={form.gstNumber}
                onChange={handleChange}
                onKeyDown={blockEnterSubmit}
                enterKeyHint="next"
                placeholder="If GST-registered"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Default breakfast price
              </label>
              <input
                name="defaultBreakfastCoverPrice"
                type="text"
                inputMode="decimal"
                value={form.defaultBreakfastCoverPrice}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next !== '' && !/^\d*\.?\d*$/.test(next)) return;
                  setForm((prev) => ({ ...prev, defaultBreakfastCoverPrice: next }));
                }}
                onKeyDown={blockEnterSubmit}
                enterKeyHint="next"
                data-soft-keyboard-handled
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Payment terms</label>
            <input
              name="paymentTerms"
              value={form.paymentTerms}
              onChange={handleChange}
              onKeyDown={blockEnterSubmit}
              enterKeyHint="next"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              name="notes"
              rows={2}
              value={form.notes}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md bg-gray-200 px-4 py-2 text-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="rounded-md bg-pink-600 px-4 py-2 text-white disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingClientModal;
